import {
  configureStore,
  combineReducers,
  DevToolsEnhancerOptions,
  Middleware,
} from "@reduxjs/toolkit";
import { v4 } from "uuid";

import { createLogger } from "redux-logger";
import createSagaMiddleware from "redux-saga";

import { loadSessionStorage, setSessionStorage } from "../lib";
import { isLogoutPending, trackBootLogout } from "../lib/auth/logout_pending";
import {
  retryPendingLogout,
  retryPendingLogoutOnVisibilityChange,
  startPendingLogout,
} from "../lib/auth/pending_logout_retry";
import { clearLastActivity, isIdleExpired, readLastActivity } from "../lib/auth/idle_sign_out";
import { bindSessionScope, readSharedSessionNonce } from "../lib/auth/session_scope";
import { hydrateSessionDeadlines } from "../lib/auth/session_deadlines";
import { DeployStage, Env } from "../env";
import * as stateInterface from "./state";
import * as sagaInterface from "./saga";
import { redactForLogging } from "./redact_for_logging";

// Session storage only ever holds a subset of the `auth` and `session` slices, so on every boot
// -- signed in or not -- the slice defaults are spread UNDER whatever was persisted. A field
// added later (`refreshUnavailable` today) then hydrates with its default instead of `undefined`.
const sanitizePreloadedState = (preloadedState: any) => {
  const { refreshToken: _refreshToken, ...persistedAuth } = preloadedState?.auth ?? {};
  const sessionNonce =
    typeof persistedAuth.sessionNonce === "string" ? persistedAuth.sessionNonce : null;

  return {
    ...preloadedState,
    auth: {
      ...stateInterface.authDefaultState,
      ...persistedAuth,
      sessionNonce,
      sessionDeadlines: hydrateSessionDeadlines(persistedAuth.sessionDeadlines, sessionNonce),
    },
    session: {
      ...stateInterface.sessionStateDefaultState,
      ...(preloadedState?.session ?? {}),
    },
  };
};

// A barrel's export names in sorted order. Webpack built its namespace objects sorted, while
// CommonJS keeps source order and other bundlers promise nothing, so sorting here keeps the order
// below, and with it which saga starts first, the same everywhere.
const sortedExports = (barrel: { [key: string]: any }) =>
  Object.fromEntries(
    Object.keys(barrel)
      .sort()
      .map((key) => [key, barrel[key]]),
  );

const storeInterface: { [key: string]: any } = {
  ...sortedExports(sagaInterface),
  ...sortedExports(stateInterface),
};

// combine all functions in src/store/{state,saga} that end en Reduce into one Object
// keys will be function name without the 'Reducer' suffix
// for example the 'themeReducer' function well be come a key called 'theme'
const reducer = combineReducers(
  Object.keys(storeInterface).reduce((result, key) => {
    const current = key.endsWith("Reducer")
      ? ({ [key.replace("Reducer", "")]: storeInterface[key] } as { [key: string]: any })
      : {};
    return {
      ...current,
      ...result,
    };
  }, {}),
);

// export type RootState = ReturnType<typeof reducer>

const sagaFunctionList = Object.keys(storeInterface).reduce((result: any, key) => {
  const current = key.endsWith("Saga") ? [storeInterface[key]] : [];
  return [...current, ...result];
}, []);

// The console logger and the DevTools bridge are attached on every stage below production -- test,
// develop and staging keep their logs -- and on production neither is attached, so no dispatched
// action (the login password among them) and no state snapshot can reach a real user's console or
// a browser extension. Production is the topmost stage, so "below production" is
// simply "not production", and taking the stage as an argument keeps the rule testable.
export const loggingEnabledForStage = (stage: DeployStage): boolean =>
  stage !== DeployStage.Production;

export const createAppStore = (
  loggingEnabled: boolean = loggingEnabledForStage(Env.shared.deployStage),
) => {
  const saga = createSagaMiddleware({
    onError: (error) => {
      throw error;
    },
  });
  const preloadedState: any = sanitizePreloadedState(
    loadSessionStorage() ? loadSessionStorage() : {},
  );
  if (preloadedState?.totalParticipants) {
    delete preloadedState.totalParticipants;
    delete preloadedState.currentWorkspaceId;
  }
  const hasAccessToken = Boolean(preloadedState?.auth?.accessToken);
  // a tab that boots with the marker set retries the logout instead of refreshing. `auth`
  // stays idle -- no `isRefreshing`, no refresh dispatch -- so `ProtectedRoute` shows the login
  // page while the retry goes out, and a still-live cookie cannot sign the user back in.
  // This branch lives here rather than in a saga because `createTestStore` runs every `*Saga`,
  // so a boot saga would fire in every test.
  const logoutPending = isLogoutPending();
  // Every tab was closed while still signed in, and nobody has touched one for longer than the idle
  // limit. The stamp sees only this frontend, and the server may have kept the session alive for work
  // in Explorer, so the boot refresh below asks it before anything ends: it carries the stamp, and its
  // reply either signs the tab in or ends the session the way an unconfirmed sign-out is ended
  // (`identity_refresh_token_submit.ts`). A boot that already found the marker is ending the session
  // anyway, and only clears the stamp so the next boot does not blame inactivity again.
  const lastActivity = readLastActivity();
  const idleExpired = isIdleExpired(lastActivity, Date.now());
  const bootStaleStamp =
    lastActivity && idleExpired && !logoutPending ? { at: lastActivity.at } : null;
  const pendingLogoutPastIdle = idleExpired && logoutPending;
  if (pendingLogoutPastIdle) clearLastActivity();
  // A persisted token is trusted only for the browser sign-in it was issued under. Another tab may
  // have signed in since -- as someone else, or as the same user, ending this token's session -- and
  // a token persisted before tabs recorded their sign-in proves nothing. Either way it is dropped
  // before anything private can run on it, and the boot refresh below asks whose the cookie is now;
  // it signs the tab out rather than adopt another identity. Without storage there is nothing to
  // compare, and each tab goes by its own record. A boot past the idle limit drops it too, until the
  // server has said the session is still live.
  const persistedNonce: string | null = preloadedState?.auth?.sessionNonce ?? null;
  const sharedNonce = readSharedSessionNonce();
  const tokenTrusted =
    hasAccessToken &&
    !logoutPending &&
    !bootStaleStamp &&
    persistedNonce !== null &&
    (sharedNonce === undefined || sharedNonce === persistedNonce);
  if (hasAccessToken && !logoutPending && !tokenTrusted) {
    preloadedState.auth = { ...stateInterface.authDefaultState };
    setSessionStorage({ auth: null });
  }
  if (tokenTrusted) bindSessionScope(persistedNonce as string);
  const shouldBootRefresh = !tokenTrusted && !logoutPending;
  if (logoutPending) {
    // The marker is shared across tabs (`localStorage`) while the token is this tab's own
    // (`sessionStorage`), so a sibling tab reloading after another tab's unconfirmed logout boots
    // holding both. Dropping the persisted `auth` is what makes the paragraph above true: with the
    // token left in place `ProtectedRoute` renders the app instead of the login page, and since no
    // refresh cycle starts, the coordinator's `isLogoutPending()` gate never runs either. The
    // persisted copy goes too, or a reload after the logout is confirmed finds the token again and
    // `LoginPage` signs in on it.
    preloadedState.auth = { ...stateInterface.authDefaultState };
    setSessionStorage({ auth: null });
  }
  if (shouldBootRefresh) {
    preloadedState.auth = {
      ...stateInterface.authDefaultState,
      ...preloadedState.auth,
      isRefreshing: true,
    };
  }
  const store = configureStore({
    reducer,
    middleware: (getDefaultMiddleware) => {
      const middleware = getDefaultMiddleware({
        serializableCheck: {
          ignoredActionPaths: ["setRequestResult"],
        },
      }).concat(saga as Middleware);

      if (!loggingEnabled) {
        return middleware;
      }

      // Where the logger stays on, the action and both state snapshots are redacted before they
      // are printed, so a proof value never reaches a develop or staging console either.
      const logger = createLogger({
        actionTransformer: redactForLogging,
        stateTransformer: redactForLogging,
      });
      return middleware.concat(logger as Middleware);
    },
    // The DevTools bridge gets the same redactor as the console logger beside it. A bare `true`
    // hands the browser extension the unredacted action -- `currentPassword` among them -- on
    // exactly the stages where the confirm step is switched on.
    devTools: loggingEnabled && {
      actionSanitizer: redactForLogging as NonNullable<DevToolsEnhancerOptions["actionSanitizer"]>,
      stateSanitizer: redactForLogging as NonNullable<DevToolsEnhancerOptions["stateSanitizer"]>,
    },
    preloadedState,
  });
  sagaFunctionList.forEach(saga.run);
  if (pendingLogoutPastIdle) {
    store.dispatch(
      stateInterface.doSESSION_END_REASON_SET({
        reason: stateInterface.SessionEndReason.Inactivity,
        at: Date.now(),
      }),
    );
  }
  // the login sagas wait on this, so an ordinary boot has to put the record back to empty
  if (logoutPending) {
    startPendingLogout();
  } else {
    trackBootLogout(null);
  }
  // the same function every time, so the page holds one listener each however many stores are created
  window.addEventListener("online", retryPendingLogout);
  document.addEventListener("visibilitychange", retryPendingLogoutOnVisibilityChange);
  if (shouldBootRefresh) {
    store.dispatch(
      sagaInterface.doREQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT({
        requestId: v4(),
        ...(bootStaleStamp ? { bootStaleStamp } : {}),
      }),
    );
  }
  return store;
};

export const createTestStore = (initialState?: any) => {
  const logger = createLogger();
  const preloadedState: any = sanitizePreloadedState(
    loadSessionStorage() ? loadSessionStorage() : {},
  );
  const saga = createSagaMiddleware();
  const store = configureStore({
    reducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActionPaths: ["setRequestResult"],
        },
      }).concat(saga as Middleware),
    preloadedState: { ...preloadedState, ...initialState },
  });
  sagaFunctionList.forEach(saga.run);

  return store;
};
